import { isUrlInDomainList } from '@/hooks/useRequiredInterceptor';
import axios, { AxiosError } from 'axios';
import React from 'react';

export const useOidcErrorInterceptor = ({
  isAuthenticated,
  onRequired,
  trustedDomains,
}: {
  isAuthenticated: boolean;
  onRequired: () => void;
  trustedDomains: string[];
}) => {
  const isAuthenticatedRef = React.useRef(false);
  const waitingForAuthentication = React.useRef<(() => void)[]>([]);

  React.useEffect(() => {
    if (isAuthenticated) {
      // Resolve pending requests if isReady is true
      isAuthenticatedRef.current = isAuthenticated;
      waitingForAuthentication.current.forEach((resolve) => resolve());
      waitingForAuthentication.current = [];
    }
  }, [isAuthenticated]);

  // Handle 401 Error
  const handle401Error = React.useCallback(
    async (error: AxiosError) => {
      if (isAuthenticatedRef.current) return Promise.reject(error);

      // Wait for Authentication
      await new Promise<void>((resolve) => {
        waitingForAuthentication.current.push(resolve);

        // Authentication required
        onRequired();
      });

      // If Authenticated, then reject
      return Promise.reject(error);
    },
    [onRequired],
  );

  // Interceptor
  React.useEffect(() => {
    const ejectId = axios.interceptors.response.use(
      (config) => config,
      async (error: AxiosError) => {
        if (!isUrlInDomainList(error.config?.url, trustedDomains)) return Promise.reject(error);

        switch (error.response?.status) {
          case 401:
            return handle401Error(error);
          default:
            return Promise.reject(error);
        }
      },
    );

    return () => {
      axios.interceptors.response.eject(ejectId);
    };
  }, [handle401Error, onRequired, trustedDomains]);
};
